import { ChevronDown, LogIn, Menu, Moon, Settings, Sun } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Dashboard header: hamburger (mobile sidebar), wordmark, theme toggle and —
 * when signed in — a user menu with "Sign out".
 */
const Header = ({ onMenuToggle }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const [showUserMenu, setShowUserMenu] = useState(false);

    useEffect(() => {
        if (!showUserMenu) return undefined;
        const handleClickOutside = (event) => {
            if (!event.target.closest('.user-menu')) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showUserMenu]);

    const handleLogout = async () => {
        setShowUserMenu(false);
        await logout();
        navigate('/');
    };

    const initial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U';

    return (
        <header className="relative border-b border-edge bg-surface-card shadow-sm">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Left: mobile hamburger + wordmark */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                            type="button"
                            aria-label="Open menu"
                            onClick={() => {
                                if (onMenuToggle) onMenuToggle();
                            }}
                            className="inline-flex items-center justify-center p-2.5 rounded-lg bg-brand hover:bg-brand-hover text-white focus:outline-none focus:ring-2 focus:ring-brand md:hidden shadow-sm"
                            style={{ minWidth: '44px', minHeight: '44px' }}
                        >
                            <Menu className="w-6 h-6" strokeWidth={3} />
                        </button>
                        <Link
                            to="/"
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                            title="Back to landing page"
                        >
                            <div className="w-7 h-7 rounded-md bg-brand flex items-center justify-center shadow-sm">
                                <span className="text-white font-bold text-sm">B</span>
                            </div>
                            <span className="hidden sm:block font-semibold text-content-primary">BlockNotify</span>
                        </Link>
                    </div>

                    {/* Right: theme toggle + user menu */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                            aria-label="Toggle theme"
                        >
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>

                        {isAuthenticated ? (
                            <div className="relative user-menu">
                                <button
                                    type="button"
                                    onClick={() => setShowUserMenu((open) => !open)}
                                    className="flex items-center gap-1 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    aria-label="User menu"
                                    aria-expanded={showUserMenu}
                                >
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white text-sm font-medium">{initial}</span>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 hidden sm:block" />
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <p className="text-sm font-medium text-content-primary truncate">{user?.name || 'Account'}</p>
                                            {user?.email && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                                            )}
                                        </div>
                                        <div className="py-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowUserMenu(false);
                                                    navigate('/dashboard/settings');
                                                }}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                            >
                                                <Settings className="w-4 h-4" />
                                                Settings
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleLogout}
                                                className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                            >
                                                Sign out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand-hover transition-colors"
                            >
                                <LogIn className="w-4 h-4" />
                                Login
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
